import React from "react";

import { DSCopyButton } from "./DSCopyButton";
import { MarkdownContent } from "../atoms/MarkdownContent";

// ============================================================================
// Types
// ============================================================================

export type SoapSection =
  | "subjective"
  | "objective"
  | "assessment"
  | "plan"
  | "additional";

export type DSNoteDisplaySize = "default" | "compact";
export type DSNoteDisplayVariant = "soap" | "freeform";
export type DSNoteDisplayLayout = "grid" | "stacked";

/**
 * Note/template data for display purposes.
 */
export interface NoteDisplayData {
  // SOAP fields
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
  additionalNotes?: string;
  // Freeform content
  content?: string;
}

export interface DSNoteDisplayProps {
  /** The note/template data to display */
  data: NoteDisplayData;
  /** Display variant - soap shows color-coded sections, freeform shows single text block */
  variant?: DSNoteDisplayVariant;
  /** Layout - grid shows S O A P horizontally, stacked shows vertically */
  layout?: DSNoteDisplayLayout;
  /** Size variant - compact uses smaller text and tighter spacing */
  size?: DSNoteDisplaySize;
  /** Optional extra content rendered alongside freeform text (e.g. dental chart) */
  extra?: React.ReactNode;
  /** Test ID for automated testing */
  dataTestId: string;
  /** Whether to show the additional notes section (default: true) */
  showAdditionalNotes?: boolean;
  /** When true, content is treated as plain text (no markdown rendering or copy-as-markdown) */
  plainText?: boolean;
  /** Whether to show the copy button on each section (default: true) */
  showCopyButton?: boolean;
  /** Optional ref for the container element */
  ref?: React.Ref<HTMLDivElement>;
}

// ============================================================================
// Constants
// ============================================================================

const soapConfig: Record<
  SoapSection,
  {
    letter: string;
    label: string;
    containerClass: string;
    textClass: string;
  }
> = {
  subjective: {
    letter: "S",
    label: "Subjective",
    containerClass:
      "bg-gradient-subjective border border-subjective-subtle",
    textClass: "text-subjective",
  },
  objective: {
    letter: "O",
    label: "Objective",
    containerClass:
      "bg-gradient-objective border border-objective-subtle",
    textClass: "text-objective",
  },
  assessment: {
    letter: "A",
    label: "Assessment",
    containerClass:
      "bg-gradient-assessment border border-assessment-subtle",
    textClass: "text-assessment",
  },
  plan: {
    letter: "P",
    label: "Plan",
    containerClass: "bg-gradient-plan border border-plan-subtle",
    textClass: "text-plan",
  },
  additional: {
    letter: "+",
    label: "Additional Notes",
    containerClass:
      "bg-gradient-additional border border-additional-subtle",
    textClass: "text-additional",
  },
};

// ============================================================================
// Internal Components
// ============================================================================

interface SoapBlockProps {
  section: SoapSection;
  text?: string;
  size: DSNoteDisplaySize;
  fullWidth?: boolean;
  dataTestId: string;
  plainText?: boolean;
  showCopyButton?: boolean;
}

const SoapBlock = ({
  section,
  text,
  size,
  fullWidth,
  dataTestId,
  plainText = false,
  showCopyButton = true,
}: SoapBlockProps) => {
  const config = soapConfig[section];
  const isCompact = size === "compact";

  return (
    <section
      className={`
        flex flex-row items-start gap-md rounded-xl
        ${isCompact ? "p-sm" : "p-md"}
        ${config.containerClass}
        ${fullWidth ? "col-span-1 md:col-span-2 lg:col-span-4" : ""}
      `}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
      aria-labelledby={`${dataTestId}-label`}
    >
      {/* Letter badge */}
      <span
        id={`${dataTestId}-label`}
        className={`text-xl font-bold shrink-0 select-none ${config.textClass}`}
        aria-label={config.label}
      >
        {config.letter}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {text ? (
          <MarkdownContent
            content={text}
            className={`
              ${isCompact ? "text-xs" : "text-xs md:text-sm"}
              text-primary
            `}
            plainText={plainText}
            copyFormat
          />
        ) : (
          <span className="text-secondary text-sm">
            No data recorded.
          </span>
        )}
      </div>

      {/* Copy buttons - only show when there's text to copy */}
      {showCopyButton && text && (
        <div
          className={`flex gap-xs select-none ${isCompact ? "flex-col" : ""}`}
        >
          <DSCopyButton
            text={text}
            dataTestId={`${dataTestId}-copy-btn`}
          />
        </div>
      )}
    </section>
  );
};

// ============================================================================
// Freeform Display
// ============================================================================

interface FreeformDisplayProps {
  content?: string;
  extra?: React.ReactNode;
  size: DSNoteDisplaySize;
  dataTestId: string;
  plainText?: boolean;
  showCopyButton?: boolean;
  ref?: React.Ref<HTMLElement>;
}

const FreeformDisplay = ({
  content,
  extra,
  size,
  dataTestId,
  plainText = false,
  showCopyButton = true,
  ref,
}: FreeformDisplayProps) => {
  const isCompact = size === "compact";

  return (
    <section
      ref={ref}
      className={`
        flex flex-col md:flex-row items-start gap-md rounded-xl
        ${isCompact ? "p-sm" : "p-md"}
        bg-gradient-freeform border border-freeform-subtle
      `}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
      aria-label="Note content"
    >
      {/* Content */}
      <div className="flex flex-row items-start gap-md flex-1 min-w-0">
        <div className="flex-1 min-w-0">
          {content ? (
            <MarkdownContent
              content={content}
              className={`
                ${isCompact ? "text-xs" : "text-xs md:text-sm"}
                text-primary
              `}
              plainText={plainText}
              copyFormat
            />
          ) : (
            <span className="text-secondary text-sm">
              No content recorded.
            </span>
          )}
        </div>

        {/* Copy buttons - only show when there's content to copy */}
        {showCopyButton && content && (
          <div className="flex gap-xs select-none">
            <DSCopyButton
              text={content}
              dataTestId={`${dataTestId}-copy-btn`}
            />
          </div>
        )}
      </div>

      {/* Extra content (e.g. dental chart) */}
      {extra}
    </section>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const DSNoteDisplay = ({
  data,
  variant = "soap",
  layout = "grid",
  size = "default",
  extra,
  showAdditionalNotes = true,
  plainText = false,
  showCopyButton = true,
  dataTestId,
  ref,
}: DSNoteDisplayProps) => {
  // Freeform variant - single text block
  if (variant === "freeform") {
    return (
      <FreeformDisplay
        ref={ref}
        content={data.content}
        extra={extra}
        size={size}
        plainText={plainText}
        showCopyButton={showCopyButton}
        dataTestId={dataTestId}
      />
    );
  }

  // SOAP variant - color-coded sections
  const isCompact = size === "compact";
  const isStacked = layout === "stacked";

  const gridClasses = isStacked
    ? "flex flex-col gap-sm"
    : isCompact
      ? "grid gap-xs grid-cols-1 md:grid-cols-2 lg:grid-cols-4 my-sm"
      : "grid gap-sm grid-cols-1 md:grid-cols-2 lg:grid-cols-4";

  return (
    <div
      ref={ref}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
      role="region"
      aria-label="Clinical note"
    >
      <div className={gridClasses}>
        <SoapBlock
          section="subjective"
          text={data.subjective}
          size={size}
          plainText={plainText}
          showCopyButton={showCopyButton}
          dataTestId={`${dataTestId}-subjective`}
        />

        <SoapBlock
          section="objective"
          text={data.objective}
          size={size}
          plainText={plainText}
          showCopyButton={showCopyButton}
          dataTestId={`${dataTestId}-objective`}
        />

        <SoapBlock
          section="assessment"
          text={data.assessment}
          size={size}
          plainText={plainText}
          showCopyButton={showCopyButton}
          dataTestId={`${dataTestId}-assessment`}
        />

        <SoapBlock
          section="plan"
          text={data.plan}
          size={size}
          plainText={plainText}
          showCopyButton={showCopyButton}
          dataTestId={`${dataTestId}-plan`}
        />

        {showAdditionalNotes && (
          <SoapBlock
            section="additional"
            text={data.additionalNotes}
            size={size}
            plainText={plainText}
            showCopyButton={showCopyButton}
            fullWidth
            dataTestId={`${dataTestId}-additional`}
          />
        )}
      </div>
    </div>
  );
};
